import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2, ExternalLink, Newspaper, Info, FileText, Link as LinkIcon, ImagePlus, X, Loader2, Pencil, Paperclip, Download, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { mode: "external", title: "", url: "", body: "", cover_file_id: null, display_date: "", attachments: [] };

export default function NewsPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const [coverPreview, setCoverPreview] = useState("");
  const [editing, setEditing] = useState(null); // news item being edited
  const fileRef = useRef(null);
  const attFileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/news");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const uploadCover = async (file, target = "form") => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran gambar maksimal 5MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/news/cover-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (target === "edit" && editing) {
        setEditing({ ...editing, cover_file_id: data.file_id, _coverPreview: URL.createObjectURL(file) });
      } else {
        setForm((f) => ({ ...f, cover_file_id: data.file_id }));
        setCoverPreview(URL.createObjectURL(file));
      }
      const kb = Math.round(data.size / 1024);
      toast.success(`Gambar diunggah (${kb} KB setelah kompresi).`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const clearCover = (target = "form") => {
    if (target === "edit" && editing) {
      setEditing({ ...editing, cover_file_id: null, _coverPreview: "" });
    } else {
      setForm((f) => ({ ...f, cover_file_id: null }));
      if (coverPreview) URL.revokeObjectURL(coverPreview);
      setCoverPreview("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const uploadAttachment = async (file, target = "form") => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Ukuran lampiran maksimal 10MB."); return; }
    setUploadingAtt(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/news/attachment-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const att = { file_id: data.file_id, filename: data.filename, size: data.size, content_type: data.content_type };
      if (target === "edit" && editing) {
        setEditing({ ...editing, attachments: [...(editing.attachments || []), att] });
      } else {
        setForm((f) => ({ ...f, attachments: [...(f.attachments || []), att] }));
      }
      toast.success(`Lampiran diunggah: ${att.filename}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingAtt(false);
      if (attFileRef.current) attFileRef.current.value = "";
    }
  };

  const removeAttachment = (file_id, target = "form") => {
    if (target === "edit" && editing) {
      setEditing({ ...editing, attachments: (editing.attachments || []).filter((a) => a.file_id !== file_id) });
    } else {
      setForm((f) => ({ ...f, attachments: (f.attachments || []).filter((a) => a.file_id !== file_id) }));
    }
  };

  const add = async () => {
    const title = form.title.trim();
    if (!title) { toast.error("Judul wajib diisi."); return; }
    if (form.mode === "external") {
      const url = form.url.trim();
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        toast.error("URL harus diawali http:// atau https://"); return;
      }
    } else {
      if (!form.body.trim()) { toast.error("Isi artikel wajib diisi."); return; }
    }
    setAdding(true);
    try {
      const payload = { mode: form.mode, title };
      if (form.mode === "external") {
        payload.url = form.url.trim();
      } else {
        payload.body = form.body;
        if (form.attachments && form.attachments.length > 0) {
          payload.attachment_file_ids = form.attachments.map((a) => a.file_id);
        }
      }
      // Cover berlaku untuk kedua mode
      if (form.cover_file_id) payload.cover_file_id = form.cover_file_id;
      if (form.display_date) payload.display_date = form.display_date;
      await api.post("/news", payload);
      toast.success(form.mode === "internal" ? "Artikel dipublikasikan." : "Link ditambahkan.");
      setForm(EMPTY_FORM);
      clearCover();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Hapus "${n.title}"?`)) return;
    try {
      await api.delete(`/news/${n.id}`);
      toast.success("Dihapus.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const moveItem = async (id, direction) => {
    try {
      const { data } = await api.post(`/news/${id}/move?direction=${direction}`);
      if (data?.no_op) {
        toast.info("Sudah di posisi paling atas/bawah.");
        return;
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const title = (editing.title || "").trim();
    if (!title) { toast.error("Judul wajib diisi."); return; }
    const payload = { title };
    if (editing.mode === "external") {
      if (!(editing.url || "").startsWith("http")) { toast.error("URL harus http/https"); return; }
      payload.url = editing.url;
    } else {
      if (!(editing.body || "").trim()) { toast.error("Isi artikel wajib diisi."); return; }
      payload.body = editing.body;
      payload.attachment_file_ids = (editing.attachments || []).map((a) => a.file_id);
    }
    // Cover berlaku untuk kedua mode
    payload.cover_file_id = editing.cover_file_id || null;
    payload.display_date = editing.display_date || null;
    try {
      await api.put(`/news/${editing.id}`, payload);
      toast.success("Berita diperbarui.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="news-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Info Terbaru</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-2">
          <Newspaper className="h-7 w-7 text-slate-700" />
          Berita &amp; Artikel
        </h1>
        <p className="text-slate-600 text-sm mt-2">
          Tambahkan <b>Artikel Blog Internal</b> (tampil di <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">sipilkades.com/&lt;slug&gt;</code>) atau <b>Link Eksternal</b>. 5 item terbaru tampil di halaman utama.
        </p>
      </header>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
            <Plus className="h-4 w-4" /> Tambah Berita
          </div>

          {/* Mode selector */}
          <div className="grid grid-cols-2 gap-2" data-testid="news-mode-selector">
            <ModePill
              active={form.mode === "internal"}
              onClick={() => setForm({ ...form, mode: "internal" })}
              icon={<FileText className="h-4 w-4" />}
              label="Artikel Blog"
              desc="Tulis konten di sini"
              testid="news-mode-internal"
            />
            <ModePill
              active={form.mode === "external"}
              onClick={() => setForm({ ...form, mode: "external" })}
              icon={<LinkIcon className="h-4 w-4" />}
              label="Link Eksternal"
              desc="Arahkan ke URL luar"
              testid="news-mode-external"
            />
          </div>

          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value.slice(0, 200) })}
            placeholder="Judul (maks 200 karakter)"
            className="h-10"
            maxLength={200}
            data-testid="news-add-title"
          />

          {/* Tanggal berita (opsional, override created_at) */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-slate-700 whitespace-nowrap" htmlFor="news-add-date">
              Tanggal Terbit:
            </label>
            <Input
              id="news-add-date"
              type="date"
              value={form.display_date || ""}
              onChange={(e) => setForm({ ...form, display_date: e.target.value })}
              className="h-9 max-w-[180px]"
              data-testid="news-add-date"
            />
            {form.display_date && (
              <button
                type="button"
                onClick={() => setForm({ ...form, display_date: "" })}
                className="text-xs text-slate-500 hover:text-slate-900 underline"
              >
                Reset
              </button>
            )}
            <span className="text-[11px] text-slate-500">Kosongkan &rarr; pakai tanggal saat dibuat.</span>
          </div>

          {form.mode === "external" ? (
            <Input
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value.slice(0, 500) })}
              placeholder="https://contoh.com/berita-detail"
              className="h-10 font-mono text-sm"
              type="url"
              data-testid="news-add-url"
            />
          ) : (
            <>
              <textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value.slice(0, 50000) })}
                placeholder="Tulis isi artikel di sini. Enter untuk paragraf baru."
                rows={8}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 leading-relaxed"
                maxLength={50000}
                data-testid="news-add-body"
              />
              <div className="text-[11px] text-slate-400 text-right">{form.body.length.toLocaleString("id-ID")} / 50.000 karakter</div>
            </>
          )}

          {/* Cover upload — untuk kedua mode (internal & external) */}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="news-cover-uploader">
            <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <ImagePlus className="h-3.5 w-3.5" /> Cover Image (opsional)
            </div>
            {coverPreview || form.cover_file_id ? (
              <div className="relative">
                <img
                  src={coverPreview}
                  alt="cover preview"
                  className="w-full max-h-60 object-cover rounded-md border border-slate-200 bg-white"
                />
                <button
                  type="button"
                  onClick={clearCover}
                  className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow-md"
                  data-testid="news-cover-remove"
                  aria-label="Hapus cover"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <label
                className={`flex items-center justify-center h-24 border-2 border-dashed rounded-md cursor-pointer text-xs text-slate-500 ${uploading ? "opacity-50 pointer-events-none" : "hover:border-slate-900 hover:bg-white"}`}
                data-testid="news-cover-input-label"
              >
                {uploading ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Mengunggah...</span>
                ) : (
                  <span className="inline-flex items-center gap-1.5"><ImagePlus className="h-4 w-4" /> Klik untuk pilih gambar (jpg/png, maks 5MB)</span>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => uploadCover(e.target.files?.[0])}
                  data-testid="news-cover-input"
                />
              </label>
            )}
            <p className="text-[11px] text-slate-500">
              Gambar akan otomatis dikompresi (max 1600px, ~200KB) agar cepat dimuat di mobile.
            </p>
          </div>

          {/* Attachments — hanya internal (artikel blog) */}
          {form.mode === "internal" && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="news-attachments-uploader">
              <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <Paperclip className="h-3.5 w-3.5" /> Lampiran (opsional, max 10MB per file)
              </div>
              {(form.attachments || []).length > 0 && (
                <ul className="space-y-1.5">
                  {form.attachments.map((a) => (
                    <li key={a.file_id} className="flex items-center gap-2 rounded-md bg-white border border-slate-200 px-2.5 py-1.5">
                      <Paperclip className="h-3.5 w-3.5 flex-shrink-0 text-slate-500" />
                      <span className="text-xs font-medium text-slate-800 truncate flex-1">{a.filename}</span>
                      <span className="text-[10px] text-slate-500">{fmtBytes(a.size)}</span>
                      <button type="button" onClick={() => removeAttachment(a.file_id, "form")} className="text-red-600 hover:text-red-800" aria-label="Hapus">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <label className={`inline-flex items-center gap-1.5 text-xs rounded-md border border-dashed border-slate-300 px-3 py-2 cursor-pointer hover:border-slate-900 hover:bg-white ${uploadingAtt ? "opacity-50 pointer-events-none" : ""}`}>
                {uploadingAtt ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
                {uploadingAtt ? "Mengunggah..." : "Tambah Lampiran (PDF, DOCX, XLSX, gambar, ...)"}
                <input
                  ref={attFileRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.gif,.txt,.csv,.zip"
                  className="hidden"
                  onChange={(e) => uploadAttachment(e.target.files?.[0], "form")}
                  data-testid="news-attachment-input"
                />
              </label>
            </div>
          )}

          <Button
            onClick={add}
            disabled={adding || !form.title.trim() || (form.mode === "external" ? !form.url.trim() : !form.body.trim())}
            className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 w-full sm:w-auto"
            data-testid="news-add-submit"
          >
            {adding ? "Menyimpan..." : (<><Plus className="h-4 w-4 mr-1.5" /> {form.mode === "internal" ? "Publikasikan Artikel" : "Tambah Link"}</>)}
          </Button>

          <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2.5">
            <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            {form.mode === "internal" ? (
              <span>Artikel akan dapat diakses di URL <code className="font-mono text-[10px] bg-white px-1 rounded">sipilkades.com/&lt;slug&gt;</code>. Slug otomatis dari judul.</span>
            ) : (
              <span>URL bebas mengarah ke website manapun (Facebook, WordPress, dsb). Wajib diawali <code className="font-mono text-[10px] bg-white px-1 rounded">https://</code>.</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="news-table">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold w-12">#</th>
                  <th className="px-3 py-2 text-left font-semibold">Judul</th>
                  <th className="px-3 py-2 text-left font-semibold w-28">Tipe</th>
                  <th className="px-3 py-2 text-left font-semibold">Target</th>
                  <th className="px-3 py-2 text-left font-semibold w-40">Dibuat</th>
                  <th className="px-3 py-2 text-center font-semibold w-16">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {!loading && items.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-10 text-slate-500">Belum ada berita.</td></tr>
                )}
                {loading && (
                  <tr><td colSpan={6} className="text-center py-10 text-slate-400">Memuat...</td></tr>
                )}
                {items.map((n, idx) => (
                  <tr key={n.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`news-row-${n.id}`}>
                    <td className="px-3 py-2 text-slate-500">
                      {idx + 1}
                      {idx < 6 && <span className="ml-1 inline-block text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1">LIVE</span>}
                    </td>
                    <td className="px-3 py-2 font-medium">{n.title}</td>
                    <td className="px-3 py-2">
                      {n.mode === "internal" ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
                          <FileText className="h-3 w-3" /> Artikel
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                          <LinkIcon className="h-3 w-3" /> Link
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {n.mode === "internal" ? (
                        <a
                          href={`/${n.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-700 hover:text-blue-900 underline text-xs font-mono inline-flex items-center gap-1"
                        >
                          <ExternalLink className="h-3 w-3" /> /{n.slug}
                        </a>
                      ) : (
                        <a
                          href={n.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-700 hover:text-blue-900 underline text-xs font-mono inline-flex items-center gap-1"
                        >
                          <ExternalLink className="h-3 w-3" />
                          <span className="truncate max-w-[240px]">{n.url}</span>
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-xs">
                      {new Date(n.created_at).toLocaleString("id-ID", {
                        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta",
                      })}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" disabled={idx === 0}
                          onClick={() => moveItem(n.id, "up")}
                          className="h-7 px-2 text-slate-700 hover:bg-slate-100 disabled:opacity-30"
                          title="Naik satu posisi"
                          data-testid={`news-move-up-${n.id}`}>
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" disabled={idx === items.length - 1}
                          onClick={() => moveItem(n.id, "down")}
                          className="h-7 px-2 text-slate-700 hover:bg-slate-100 disabled:opacity-30"
                          title="Turun satu posisi"
                          data-testid={`news-move-down-${n.id}`}>
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => {
                          setEditing({
                            ...n,
                            attachments: n.attachments || [],
                          });
                        }} className="h-7 px-2 text-slate-700 hover:bg-slate-100" data-testid={`news-edit-${n.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(n)} className="h-7 px-2 text-red-600 hover:bg-red-50" data-testid={`news-delete-${n.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}
          data-testid="edit-news-modal"
        >
          <div className="w-full max-w-2xl my-6 rounded-lg bg-white shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Edit Berita</div>
                <div className="font-display text-lg font-bold mt-0.5">{editing.mode === "internal" ? "Artikel" : "Link Eksternal"}</div>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700 text-xl leading-none px-2" data-testid="edit-news-close">×</button>
            </div>
            <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="text-xs font-semibold text-slate-700">Judul</label>
                <Input
                  value={editing.title || ""}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value.slice(0, 200) })}
                  className="h-10 mt-1"
                  maxLength={200}
                  data-testid="edit-news-title"
                />
              </div>

              {/* Tanggal Terbit — bisa di-override admin */}
              <div>
                <label className="text-xs font-semibold text-slate-700" htmlFor="edit-news-date">Tanggal Terbit</label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="edit-news-date"
                    type="date"
                    value={(editing.display_date || "").slice(0, 10)}
                    onChange={(e) => setEditing({ ...editing, display_date: e.target.value })}
                    className="h-10 max-w-[200px]"
                    data-testid="edit-news-date"
                  />
                  {editing.display_date && (
                    <button
                      type="button"
                      onClick={() => setEditing({ ...editing, display_date: "" })}
                      className="text-xs text-slate-500 hover:text-slate-900 underline"
                    >
                      Reset (pakai tanggal dibuat)
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Kosongkan &rarr; pakai tanggal saat berita dibuat ({editing.created_at ? new Date(editing.created_at).toLocaleDateString("id-ID") : "-"}).
                </p>
              </div>
              {editing.mode === "external" ? (
                <div>
                  <label className="text-xs font-semibold text-slate-700">URL</label>
                  <Input
                    value={editing.url || ""}
                    onChange={(e) => setEditing({ ...editing, url: e.target.value.slice(0, 500) })}
                    className="h-10 mt-1 font-mono text-sm"
                    data-testid="edit-news-url"
                  />
                </div>
              ) : (
                <div>
                  <label className="text-xs font-semibold text-slate-700">Isi Artikel</label>
                  <textarea
                    value={editing.body || ""}
                    onChange={(e) => setEditing({ ...editing, body: e.target.value.slice(0, 50000) })}
                    rows={8}
                    className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 leading-relaxed"
                    maxLength={50000}
                    data-testid="edit-news-body"
                  />
                  <div className="text-[11px] text-slate-400 text-right">{(editing.body || "").length.toLocaleString("id-ID")} / 50.000</div>
                </div>
              )}

              {/* Cover uploader — kedua mode */}
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="edit-news-cover-uploader">
                <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <ImagePlus className="h-3.5 w-3.5" /> Cover Image (opsional)
                </div>
                {(editing._coverPreview || editing.cover_file_id) ? (
                  <div className="relative">
                    <img
                      src={editing._coverPreview || `${api.defaults.baseURL}/public/news/cover/${editing.id}?v=${editing.cover_updated_at || editing.updated_at || Date.now()}`}
                      alt="cover"
                      className="w-full max-h-60 object-cover rounded-md border border-slate-200 bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => clearCover("edit")}
                      className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow-md"
                      data-testid="edit-news-cover-remove"
                      aria-label="Hapus cover"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    <label className={`absolute bottom-2 right-2 inline-flex items-center gap-1 bg-slate-900/85 hover:bg-slate-900 text-white text-[11px] px-2 py-1 rounded cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                      {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImagePlus className="h-3 w-3" />}
                      {uploading ? "Mengunggah..." : "Ganti"}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => uploadCover(e.target.files?.[0], "edit")}
                        data-testid="edit-news-cover-change"
                      />
                    </label>
                  </div>
                ) : (
                  <label
                    className={`flex items-center justify-center h-24 border-2 border-dashed rounded-md cursor-pointer text-xs text-slate-500 ${uploading ? "opacity-50 pointer-events-none" : "hover:border-slate-900 hover:bg-white"}`}
                  >
                    {uploading ? (
                      <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Mengunggah...</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5"><ImagePlus className="h-4 w-4" /> Klik untuk pilih gambar</span>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => uploadCover(e.target.files?.[0], "edit")}
                      data-testid="edit-news-cover-input"
                    />
                  </label>
                )}
              </div>

              {/* Attachments editor — hanya internal */}
              {editing.mode === "internal" && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
                  <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                    <Paperclip className="h-3.5 w-3.5" /> Lampiran
                  </div>
                  {(editing.attachments || []).length > 0 && (
                    <ul className="space-y-1.5">
                      {editing.attachments.map((a) => (
                        <li key={a.file_id} className="flex items-center gap-2 rounded-md bg-white border border-slate-200 px-2.5 py-1.5">
                          <Paperclip className="h-3.5 w-3.5 flex-shrink-0 text-slate-500" />
                          <span className="text-xs font-medium text-slate-800 truncate flex-1">{a.filename}</span>
                          <span className="text-[10px] text-slate-500">{fmtBytes(a.size)}</span>
                          <button type="button" onClick={() => removeAttachment(a.file_id, "edit")} className="text-red-600 hover:text-red-800" aria-label="Hapus">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <label className={`inline-flex items-center gap-1.5 text-xs rounded-md border border-dashed border-slate-300 px-3 py-2 cursor-pointer hover:border-slate-900 hover:bg-white ${uploadingAtt ? "opacity-50 pointer-events-none" : ""}`}>
                    {uploadingAtt ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
                    {uploadingAtt ? "Mengunggah..." : "Tambah Lampiran"}
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.gif,.txt,.csv,.zip"
                      className="hidden"
                      onChange={(e) => uploadAttachment(e.target.files?.[0], "edit")}
                      data-testid="edit-news-attachment-input"
                    />
                  </label>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
              <Button variant="outline" onClick={() => setEditing(null)} className="h-10" data-testid="edit-news-cancel">Batal</Button>
              <Button onClick={saveEdit} className="bg-slate-900 hover:bg-slate-800 h-10" data-testid="edit-news-save">
                <Pencil className="h-4 w-4 mr-2" /> Simpan
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtBytes(bytes) {
  if (!bytes) return "-";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

function ModePill({ active, onClick, icon, label, desc, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-md border p-2.5 transition ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-sm font-semibold">{icon} {label}</div>
      <div className={`text-[11px] mt-0.5 ${active ? "text-slate-300" : "text-slate-500"}`}>{desc}</div>
    </button>
  );
}
